import { FormikCheckboxField } from '@superdispatch/forms';
import { Stack } from '@superdispatch/ui';
import { Box } from '@superdispatch/ui-lab';
import { castAPIData } from 'shared/api/APISchema';
import { useAppFormik } from 'shared/form/AppFormik';
import {
  FormikDrawer,
  FormikDrawerActions,
  FormikDrawerContent,
} from 'shared/form/FormikDrawer';
import { z } from 'zod';
import { loadDeliveryEditSchema } from '../data/LoadActionsDTO';
import {
  formatLoadStatus,
  isEmptyLoadLeg,
  loadVenueSchema,
} from '../data/LoadDTO';
import { trackLoadsEvent } from '../data/LoadsAnalytics';
import { useLoadDetails, useLoadsAPI, useLoadsCache } from '../data/LoadsAPI';
import { useLoadStageFromBackURL } from '../view/ViewLoadHeaderActions';
import { LoadDeliveryForm } from './LoadDeliveryForm';
import { useFlag } from 'shared/settings/FeatureToggles';
import { useSubhaulLoad } from '../view/useSubhaulLoad';

const editLoadDeliverySchema = loadDeliveryEditSchema.extend({
  $copyToCustomer: z.boolean().default(false),
});

interface EditLoadDeliveryDrawerContentProps {
  loadGUID: string;
  onClose: () => void;
}

function EditLoadDeliveryDrawerContent({
  onClose,
  loadGUID,
}: EditLoadDeliveryDrawerContentProps) {
  const { isSubhauled } = useSubhaulLoad();
  const { data } = useLoadDetails(loadGUID, { isSubhauled });
  const { invalidateLoads } = useLoadsCache();
  const { editLoadDelivery, editLoadCustomer } = useLoadsAPI();
  const loadStage = useLoadStageFromBackURL();
  const isRestrictedAddressEditing = useFlag(
    'carrier_restrict_shipper_address_editing',
  );
  const formik = useAppFormik({
    initialValues: data?.delivery,
    validationSchema: editLoadDeliverySchema,
    async onSubmit({ $copyToCustomer, ...values }) {
      const response = await editLoadDelivery(loadGUID, values);

      if ($copyToCustomer) {
        await editLoadCustomer(loadGUID, {
          save_customer_contact: false,
          venue: castAPIData(loadVenueSchema, values.venue),
        });
      }

      return response;
    },
    onSubmitSuccess() {
      onClose();
      invalidateLoads();

      trackLoadsEvent({
        name: 'Carrier Edited Load',
        utm_medium: loadStage,
        utm_content: 'View Page',
        load_status: formatLoadStatus(data?.status),
        is_created_by_broker: !!data?.is_created_by_broker,
        delivery_information:
          !data?.delivery || isEmptyLoadLeg(data.delivery) ? 'Added' : 'Edited',
      });
    },
    getSuccessMessage(_, { $copyToCustomer }) {
      return $copyToCustomer
        ? 'Successfully changed delivery and customer information'
        : 'Successfully changed delivery information.';
    },
    getErrorMessage({ message }) {
      return message || 'Something went wrong, contact support.';
    },
  });

  return (
    <FormikDrawerContent
      formik={formik}
      title="Delivery Information"
      onClose={onClose}
      actions={<FormikDrawerActions name="loads" permission="can_manage" />}
    >
      <Box maxWidth="424px">
        <Stack space="large">
          {!data?.is_received_via_subhaul && !isSubhauled && (
            <FormikCheckboxField
              name="$copyToCustomer"
              label="Copy to Customer"
              helperText="Customer will have the same information as below"
            />
          )}

          <LoadDeliveryForm
            isEDI={!!data?.is_edi}
            isSubhauled={data?.is_received_via_subhaul}
            isDisabledAddressDetails={isSubhauled}
            canEditAddress={
              isRestrictedAddressEditing ? !data?.is_created_by_broker : true
            }
          />
        </Stack>
      </Box>
    </FormikDrawerContent>
  );
}

interface EditLoadDeliveryDrawerProps extends EditLoadDeliveryDrawerContentProps {
  open: boolean;
}

export function EditLoadDeliveryDrawer({
  open,
  onClose,
  loadGUID,
}: EditLoadDeliveryDrawerProps) {
  return (
    <FormikDrawer open={open} onClose={onClose}>
      <EditLoadDeliveryDrawerContent loadGUID={loadGUID} onClose={onClose} />
    </FormikDrawer>
  );
}
